import type { ReactNode } from "react";
import { CircleCheckIcon, TriangleAlertIcon } from "lucide-react";

import type { AnalyticsElementId } from "@/lib/analytics/analyticsElementId";
import { cn } from "@/lib/utils";
import { AdminButton } from "@/shared/design-system/admin/AdminButton";
import { AdminPageHeader } from "@/shared/design-system/admin/AdminPageHeader";
import { AdminThemeScope } from "@/shared/design-system/admin/AdminThemeScope";
import { Spinner } from "@/shared/ui/spinner";

type StatusPageTone = "default" | "info" | "success" | "warning" | "danger";

// The icon medallion follows the admin status tokens so every tone stays
// legible in both themes; `default` is the neutral raised surface used for
// loading spinners and informational icons.
const toneClassName: Record<StatusPageTone, string> = {
  default:
    "border border-(--app-border-subtle) bg-(--color-surface-primary) text-(--color-accent-strong) shadow-(--admin-shadow-card)",
  info: "bg-(--app-status-info-bg) text-(--app-status-info-foreground)",
  success: "bg-(--app-status-success-bg) text-(--app-status-success-foreground)",
  warning: "bg-(--app-status-warning-bg) text-(--app-status-warning-foreground)",
  danger: "bg-(--app-status-danger-bg) text-(--app-status-danger-foreground)",
};

type StatusPageKind = "loading" | "success" | "invalid-link" | "expired" | "error";

// One medallion per recurring situation, so a missing token or a failed load
// reads the same on every page. A link problem is a warning — the reader has to
// go back for a working link — and a failed load or request is danger.
const kindMedallion: Record<StatusPageKind, { tone: StatusPageTone; icon: ReactNode }> = {
  loading: { tone: "default", icon: <Spinner className="size-8" /> },
  success: { tone: "success", icon: <CircleCheckIcon className="size-8" /> },
  "invalid-link": { tone: "warning", icon: <TriangleAlertIcon className="size-8" /> },
  expired: { tone: "warning", icon: <TriangleAlertIcon className="size-8" /> },
  error: { tone: "danger", icon: <TriangleAlertIcon className="size-8" /> },
};

/** How the medallion is chosen: a `kind` preset, or a one-off `icon`/`tone`/`media`. */
export type StatusPageMedallionProps =
  | {
      /**
       * The situation the page reports, which fixes the medallion's tone and icon:
       * - `loading` — waiting on a load, a request or a redirect;
       * - `success` — the flow finished;
       * - `invalid-link` — the link lacks its token or names nothing we know;
       * - `expired` — the link, or the session it opened, has lapsed;
       * - `error` — a load or a request failed.
       */
      kind: StatusPageKind;
      icon?: never;
      tone?: never;
      media?: never;
    }
  | {
      kind?: never;
      /** Icon for a one-off state no `kind` covers, in a round tone-colored medallion above the title. */
      icon?: ReactNode;
      /** Status tone of that medallion. */
      tone?: StatusPageTone;
      /** Free-form block above the title (e.g. the 404 numeral) — replaces the icon medallion. */
      media?: ReactNode;
    };

export type StatusPageRetryProps =
  | { onRetry?: never; retrying?: never; retryAnalyticsElementId?: never }
  | {
      /** Re-runs the failed load or request from a Retry button in the actions row. */
      onRetry: () => void;
      /** Disables Retry while the re-run is in flight. */
      retrying?: boolean;
      /** Structured element id wiring click analytics on the Retry button. */
      retryAnalyticsElementId: AnalyticsElementId;
    };

type StatusPageProps = StatusPageMedallionProps &
  StatusPageRetryProps & {
    title: string;
    subtitle?: ReactNode;
    /** The page's action buttons as plain siblings, laid out in one wrapping, centred row under the message. */
    actions?: ReactNode;
    /** Tailwind max-width for the centered column (default `max-w-md`). */
    widthClassName?: string;
    children?: ReactNode;
  };

/**
 * Full-screen centered status card (loading, success, failure, empty, access
 * states) rendered through the admin design system — the status sibling of the
 * admin hero pages, shared by creator-facing flows and admin route guards.
 * Deliberately chrome-free (no top bar): status screens carry only their
 * message and action buttons, and several render on eager paths (404, the app
 * error boundary) that must stay light. Content pages with a real header +
 * body belong in `CreatorPage` instead.
 *
 * A recurring situation takes its `kind`; `icon` and `tone` are for the
 * one-off states no kind covers, and the two cannot be combined.
 */
export function StatusPage({
  title,
  subtitle,
  kind,
  icon,
  tone,
  media,
  actions,
  onRetry,
  retrying,
  retryAnalyticsElementId,
  widthClassName = "max-w-md",
  children,
}: StatusPageProps) {
  const medallion = kind ? kindMedallion[kind] : { tone: tone ?? "default", icon };

  return (
    <AdminThemeScope className="flex min-h-svh w-full flex-col items-center justify-center bg-(--color-background) px-4 py-8 text-(--color-text-primary)">
      <section className={cn("flex w-full flex-col items-center gap-6 text-center", widthClassName)}>
        {media ??
          (medallion.icon ? (
            <div
              data-tone={medallion.tone}
              className={cn("flex size-16 items-center justify-center rounded-full", toneClassName[medallion.tone])}
            >
              {medallion.icon}
            </div>
          ) : null)}

        {/* `align`, not just `text-center`: the title is a flex item in a row as wide as the
            description, so without it a subtitle longer than the title leaves the title flush left. */}
        <AdminPageHeader title={title} description={subtitle} titleSize="md" align="center" className="w-full" />

        {onRetry || actions ? (
          <div className="flex w-full flex-wrap items-center justify-center gap-2">
            {onRetry ? (
              <AdminButton onClick={onRetry} disabled={retrying} analyticsElementId={retryAnalyticsElementId}>
                Retry
              </AdminButton>
            ) : null}
            {actions}
          </div>
        ) : null}

        {children}
      </section>
    </AdminThemeScope>
  );
}
